import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CalendarDays, Plus, Check, X, Clock, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Leave {
  id: string;
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  days_approved?: number;
  reason?: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  comments?: string;
  employee?: {
    first_name: string;
    last_name: string;
    employee_code: string;
  };
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

export const LeaveManagement = () => {
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [leaveForm, setLeaveForm] = useState({
    employee_id: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
    days_requested: 1
  });

  const leaveTypes = [
    'Congé annuel',
    'Congé maladie',
    'Congé maternité',
    'Congé paternité',
    'Congé exceptionnel',
    'Absence autorisée',
    'Absence non autorisée'
  ];

  useEffect(() => {
    fetchLeaves();
    fetchEmployees();
  }, []);

  const fetchLeaves = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_leaves')
        .select(`
          *,
          employee:employees!employee_id (
            first_name,
            last_name,
            employee_code
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeaves(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des congés:', error);
      toast.error('Erreur lors du chargement des congés');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, employee_code')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
    }
  };

  const calculateDays = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleLeaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const days = calculateDays(leaveForm.start_date, leaveForm.end_date);
      
      const { error, data: leaveData } = await supabase
        .from('employee_leaves')
        .insert({
          ...leaveForm,
          days_requested: days
        })
        .select('*, employee:employees!employee_id(first_name, last_name, employee_code)')
        .single();

      if (error) throw error;

      // Envoyer l'email de notification
      try {
        const { error: emailError } = await supabase.functions.invoke('send-leave-notification', {
          body: {
            employee_name: `${leaveData.employee.first_name} ${leaveData.employee.last_name}`,
            employee_code: leaveData.employee.employee_code,
            leave_type: leaveData.leave_type,
            start_date: leaveData.start_date,
            end_date: leaveData.end_date,
            days_requested: days,
            reason: leaveData.reason,
            hr_email: 'rh@nazandoo.com' // À personnaliser selon votre configuration
          }
        });

        if (emailError) {
          console.error('Erreur lors de l\'envoi de l\'email:', emailError);
          // Ne pas bloquer la création même si l'email échoue
        }
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email:', emailError);
      }

      toast.success('Demande de congé créée avec succès');
      setLeaveDialog(false);
      resetForm();
      fetchLeaves();
    } catch (error) {
      console.error('Erreur lors de la création de la demande:', error);
      toast.error('Erreur lors de la création de la demande');
    }
  };

  const handleApproveLeave = async (leaveId: string, days?: number) => {
    try {
      const { error } = await supabase
        .from('employee_leaves')
        .update({
          status: 'Approuvé',
          days_approved: days,
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', leaveId);

      if (error) throw error;

      toast.success('Congé approuvé');
      fetchLeaves();
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleRejectLeave = async (leaveId: string) => {
    try {
      const { error } = await supabase
        .from('employee_leaves')
        .update({
          status: 'Refusé',
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', leaveId);

      if (error) throw error;

      toast.success('Congé refusé');
      fetchLeaves();
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      toast.error('Erreur lors du refus');
    }
  };

  const resetForm = () => {
    setLeaveForm({
      employee_id: '',
      leave_type: '',
      start_date: '',
      end_date: '',
      reason: '',
      days_requested: 1
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'En attente':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      case 'Approuvé':
        return <Badge className="bg-green-100 text-green-800"><Check className="h-3 w-3 mr-1" />Approuvé</Badge>;
      case 'Refusé':
        return <Badge variant="destructive"><X className="h-3 w-3 mr-1" />Refusé</Badge>;
      case 'Annulé':
        return <Badge variant="outline">Annulé</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getLeaveTypeColor = (type: string) => {
    switch (type) {
      case 'Congé annuel':
        return 'bg-blue-100 text-blue-800';
      case 'Congé maladie':
        return 'bg-red-100 text-red-800';
      case 'Congé maternité':
      case 'Congé paternité':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    if (leaveForm.start_date && leaveForm.end_date) {
      const days = calculateDays(leaveForm.start_date, leaveForm.end_date);
      setLeaveForm(prev => ({ ...prev, days_requested: days }));
    }
  }, [leaveForm.start_date, leaveForm.end_date]);

  if (loading) {
    return <div className="flex justify-center p-8">Chargement...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <CalendarDays className="h-5 w-5 mr-2" />
              Gestion des Congés
            </CardTitle>
          </div>
          <Dialog open={leaveDialog} onOpenChange={setLeaveDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle demande
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nouvelle demande de congé</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleLeaveSubmit} className="space-y-4">
                <div>
                  <Label>Employé</Label>
                  <Select
                    value={leaveForm.employee_id}
                    onValueChange={(value) => setLeaveForm(prev => ({ ...prev, employee_id: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name} ({employee.employee_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Type de congé</Label>
                  <Select
                    value={leaveForm.leave_type}
                    onValueChange={(value) => setLeaveForm(prev => ({ ...prev, leave_type: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Type de congé" />
                    </SelectTrigger>
                    <SelectContent>
                      {leaveTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={leaveForm.start_date}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, start_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={leaveForm.end_date}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, end_date: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Nombre de jours: {leaveForm.days_requested}</Label>
                  <p className="text-xs text-muted-foreground">Calculé automatiquement</p>
                </div>

                <div>
                  <Label>Motif</Label>
                  <Textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Motif de la demande de congé"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setLeaveDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">Créer la demande</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {leaves.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucune demande de congé</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Type de congé</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Jours</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Motif</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaves.map((leave) => (
                <TableRow key={leave.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">
                        {leave.employee?.first_name} {leave.employee?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {leave.employee?.employee_code}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getLeaveTypeColor(leave.leave_type)}>
                      {leave.leave_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{new Date(leave.start_date).toLocaleDateString('fr-FR')}</p>
                      <p className="text-muted-foreground">
                        au {new Date(leave.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="font-medium">{leave.days_requested} demandés</p>
                      {leave.days_approved && (
                        <p className="text-muted-foreground">{leave.days_approved} approuvés</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(leave.status)}</TableCell>
                  <TableCell>
                    <p className="text-sm max-w-xs truncate" title={leave.reason}>
                      {leave.reason || 'Aucun motif'}
                    </p>
                  </TableCell>
                  <TableCell>
                    {leave.status === 'En attente' && (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApproveLeave(leave.id, leave.days_requested)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectLeave(leave.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};